import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import { useCallback, useContext } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LineNightIcon from 'LuxKit/Icons/line/LineNightIcon'
import LineArrowUpRightIcon from 'LuxKit/Icons/line/LineArrowUpRightIcon'
import Typography from 'LuxKit/Typography/Typography'
import ModalContext from 'contexts/ModalContext'
import LoyaltyLandingPageMovingTiersModal from 'loyalty/pages/loyaltyLandingPageSharedSections/LoyaltyLandingPageMovingTiersModal'
import { setSvgIconSize } from 'LuxKit/SvgIcon'

const Section = styled(Pane)`
  padding: ${rem(24)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(48)} 0;
  }
`

const TierProgressionContent = styled.div`
  display: grid;
  gap: ${rem(20)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(64)};
    justify-content: center;
    grid-template-columns: repeat(3, ${rem(168)});
  }
`

const TierProgressionIcon = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.default.seven};
  border-radius: ${props => props.theme.borderRadius.round};
  height: ${rem(64)};
  width: ${rem(64)};
  flex-shrink: 0;
  ${setSvgIconSize(32)}

  ${mediaQueryUp.tablet} {
    height: ${rem(88)};
    width: ${rem(88)};
    ${setSvgIconSize(52)}
  }
`

function getProgressions(intl: ReturnType<typeof useIntl>): Array<{ description: string, icon: React.ReactNode }> {
  return [
    {
      description: intl.formatMessage({
        id: 'uARKnEL41IPx9xMjUELB',
        defaultMessage: 'Earn the required number of Status Credits to progress to the next tier',
        description: 'Tier progression step explaining Status Credits requirement',
      }),
      icon: <LineStarIcon />,
    }, {
      description: intl.formatMessage({
        id: 'saTiXk2yoK1g7hqgFf5q',
        defaultMessage: 'Stay the required number of nights to progress to the next tier',
        description: 'Tier progression step explaining nights requirement',
      }),
      icon: <LineNightIcon />,
    }, {
      description: intl.formatMessage({
        id: 'WQACQJN8mUhi9RVn6_KP',
        defaultMessage: 'Enjoy all the benefits of your new status',
        description: 'Tier progression step about enjoying new tier benefits',
      }),
      icon: <LineArrowUpRightIcon />,
    },
  ]
}

function LoyaltyLandingPageMovingTiersSection() {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const progressions = getProgressions(intl)

  const headingText = intl.formatMessage({
    id: '1GCNHgpOGPf-3nICDc84',
    defaultMessage: 'Moving up through the tiers',
    description: 'Section heading for the loyalty tier progression section',
  })
  const bodyText = intl.formatMessage({
    id: 'tstjGDSWIaXvkaNjhCP3',
    defaultMessage: 'To progress from Bronze to Silver, Gold and Platinum you’ll need to earn a certain number of Status Credits and count up your nights.',
    description: 'Description explaining how to progress through loyalty tiers',
  })
  const moreButtonText = intl.formatMessage({
    id: 'j2Yk73FZJP3kmuM1uP43',
    defaultMessage: 'More on tier progression',
    description: 'Button to open modal with detailed tier progression information',
  })

  const onMoreOnTierProgressionClick = useCallback(() => {
    showModal(<LoyaltyLandingPageMovingTiersModal />)
  }, [showModal])

  return <Section paletteType="inverse">
    <LayoutContainer variant="medium">
      <VerticalSpacer gap={24}>
        <VerticalSpacer gap={8}>
          <Heading variant="heading2" align="center">
            {headingText}
          </Heading>
          <BodyText variant="medium" align="center">
            {bodyText}
          </BodyText>
        </VerticalSpacer>
        <TierProgressionContent>
          {progressions.map((progression, index) => <Group
            key={index}
            direction="horizontal"
            verticalAlign="center"
            tabletDirection="vertical"
            tabletHorizontalAlign="center"
            gap={16}
            tabletGap={12}
          >
            <TierProgressionIcon direction="vertical" horizontalAlign="center" verticalAlign="center">
              {progression.icon}
            </TierProgressionIcon>
            <BodyText variant="medium" tabletAlign="center">{progression.description}</BodyText>
          </Group>)}
        </TierProgressionContent>
        <Typography align="center">
          <TextButton kind="secondary" onClick={onMoreOnTierProgressionClick}>
            {moreButtonText}
          </TextButton>
        </Typography>
      </VerticalSpacer>
    </LayoutContainer>
  </Section>
}

export default LoyaltyLandingPageMovingTiersSection
